import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

import { NotificationType } from '@/app/app/constants/notification.enum';
import { NotificationService } from '@/app/app/services/communications/notification.service';
import { REQUEST_TIMEOUT, SKIP_ERROR_TOAST } from '@/shared/lib/core/interceptors/header-tokens';
import { isNotNullOrEmpty } from '@/shared/lib/core/utils/data-validation.util';
import { UserStore } from '@/shared/lib/stores/user.store';

export const errorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const notificationService = inject(NotificationService);
  const userStore = inject(UserStore);
  let notificationType: NotificationType = NotificationType.Warning;
  let message: string;

  return next(req).pipe(
    timeout(req.context.get(REQUEST_TIMEOUT)),
    catchError((error) => {
      if (req.context.get(SKIP_ERROR_TOAST)) {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          userStore.clear();
        }
        return throwError(() => error);
      }

      if (!navigator.onLine) {
        notificationService.presentToast('You are currently not connected to internet', NotificationType.Warning);
      } else {
        if (error instanceof HttpErrorResponse) {
          if (!(error.error instanceof ErrorEvent)) {
            switch (error.status) {
              case 400:
                message = 'Bad Request';
                notificationType = NotificationType.Error;
                break;
              case 401:
                userStore.clear();
                message = 'You are not authorized to access this resource.';
                notificationType = NotificationType.Warning;
                break;
              case 403:
                message = 'The app encountered a problem loading some of its data. \nPlease close the app and reopen.\n\nIf the problem persist, contact support.';
                notificationType = NotificationType.Error;
                break;
              case 422:
                message = 'Your request could not be send to the device. It seems that the device is currently offline / unreachable.';
                notificationType = NotificationType.Warning;
                break;
              case 425:
                message = 'This request could not be handled. There is already an active monitoring request for this fiber.';
                notificationType = NotificationType.Warning;
                break;
              default:
                message = 'The app encountered a problem loading some of its data. \nPlease close the app and reopen.\n\nIf the problem persist, contact support.';
                notificationType = NotificationType.Error;
                break;
            }
          }
          // Server error
        } else {
          // Client Error
          if (error instanceof TimeoutError) {
            message = 'The request timed out. \nPlease check your connection and try again.';
          } else if (error.message.includes('Error: Failed to complete negotiation with the server: TypeError: Failed to fetch')) {
            message = 'The `Realtime updates` connection has run in a timeout. It will auto reconnect.';
          } else {
            message = 'We\'ve ran into a problem loading data. \nPlease try to refresh the page. \n\nIf the problem persist, contact support.';
          }

          notificationType = NotificationType.Warning;
        }
      }

      if (isNotNullOrEmpty(message)) {
        notificationService.presentToast(message, notificationType);
      }

      return EMPTY;
    }),
  );
};
